import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import * as Sentry from '@sentry/react'
import React from 'react'

import LoginPage from 'pages/development/Login'
import AdminLayout from 'containers/Layout/AdminLayout'
import { appTheme, prmTheme } from 'assets/styles/themes'
import { getDefaultRoutes, publicRoutes } from 'api/Routes'
import ProtectedRoute from 'components/ProtectedRoutes'
import NotFound from 'components/Shared/ErrorPages/404'
import NotAvailable from 'components/Shared/NotAvailable'
import { isProduction } from 'lib/utility/ApplicationConfig'
import StakeholderLayout from 'containers/Layout/StakeholderLayout'
import useValidateUserSession from 'lib/hooks/UseValidateUserSession'
import TextAnalysis from 'containers/TextAnalysis'

export const getLoginRoutes = () => {
  if (!isProduction()) {
    return (
      <Route path="/">
        <Route path="development/login" element={<LoginPage />} />
        <Route path="admin/login" element={<NotAvailable />} />
        <Route path="login" element={<NotAvailable />} />
        <Route path="/" element={<NotAvailable />} />
      </Route>
    )
  }
}

export const getAdminUIRoutes = () => {
  return (
    <Route
      path="/adm"
      element={
        <ThemeProvider theme={prmTheme}>
          <AdminLayout />
        </ThemeProvider>
      }
    >
      {getDefaultRoutes().map((route, index) => {
        return (
          <Route
            key={index}
            path={route.path}
            element={
              <ProtectedRoute name={route.name}>
                <route.component />
              </ProtectedRoute>
            }
          />
        )
      })}
      <Route path="/adm" element={<NotFound />} />
      <Route path="/adm/*" element={<NotFound />} />
    </Route>
  )
}

export const getPublicRoutes = () => {
  return (
    <Route path="/">
      <Route
        path="/adm/pv/:id"
        element={
          <ThemeProvider theme={prmTheme}>
            {<StakeholderLayout />}
          </ThemeProvider>
        }
      >
        {publicRoutes.map((route, index) => {
          return (
            <Route
              key={index}
              path={route.path}
              element={<route.component />}
            />
          )
        })}
      </Route>
      <Route
        path="/text_analysis/:source_id"
        element={
          <ThemeProvider theme={prmTheme}>
            <TextAnalysis />
          </ThemeProvider>
        }
      />
      <Route
        path="/text_analysis"
        element={
          <ThemeProvider theme={prmTheme}>
            <TextAnalysis />
          </ThemeProvider>
        }
      />
    </Route>
  )
}

const App = () => {
  const [isActiveUser, setIsActiveUser] = React.useState(false)
  // We use useLocation hook to detect route change as App component is parent component
  // of other components, hence it will require a side effect rerender itself, ref: https://reactrouter.com/en/main/hooks/use-location
  const location = useLocation()
  useValidateUserSession(location, setIsActiveUser)
  // Addition of url's in the app does not require expicit implementation for session timeout.
  const Component = () => {
    return (
      <ThemeProvider theme={appTheme}>
        <div>
          <Routes>
            {getLoginRoutes()}
            {getAdminUIRoutes()}
            {getPublicRoutes()}
          </Routes>
        </div>
      </ThemeProvider>
    )
  }

  return isActiveUser ? Component() : <></>
}

export const AppWithRouter = () => (
  <BrowserRouter basename="/">
    <App />
  </BrowserRouter>
)

export default Sentry.withProfiler(AppWithRouter, { name: 'admin-ui' })
